'use client'

import { useEffect, useState, useCallback } from 'react'
import { onAuthStateChanged, signOut, type User as FirebaseUser } from 'firebase/auth'
import { auth } from '@/lib/firebase-client'
import { Toaster, toast } from 'sonner'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'

import { LoginForm } from '@/components/auth/LoginForm'
import { AdminDashboard } from '@/components/admin/AdminDashboard'
import { EmployeeDashboard } from '@/components/employee/EmployeeDashboard'
import { NotificationPrompt } from '@/components/NotificationPrompt'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useAttendanceReminders } from '@/hooks/useAttendanceReminders'

import { InfinityLoader } from '@/components/ui/loading'

import { User as AppUser } from '@/types/user'

// Last-known profile, cached per Firebase UID. Lets a signed-in user reach their
// dashboard when the backend can't serve /api/auth/me (Firestore read-quota
// exhausted, transient 5xx, offline) instead of dead-ending at the login screen.
// This is a UI convenience only — the security boundary stays server-side: every
// API call is re-authorized by verifyAuth against the live users doc, so a forged
// cache grants no real access (privileged endpoints just 503/403 under an outage).
const PROFILE_CACHE_KEY = 'sasha.profile.cache.v1'

function readCachedProfile(uid: string): AppUser | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.uid !== uid || !parsed?.user) return null
    return parsed.user as AppUser
  } catch {
    return null
  }
}

function writeCachedProfile(uid: string, user: AppUser) {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ uid, user, cachedAt: Date.now() }))
  } catch {
    // Private-mode / storage-full — degraded-mode fallback simply won't be available.
  }
}

function clearCachedProfile() {
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY)
  } catch {
    // ignore
  }
}

async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

export { getIdToken }

function AppContent() {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [appUser, setAppUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState(false)
  const [usingCachedProfile, setUsingCachedProfile] = useState(false)

  // Schedule client-side attendance reminders (9:30 AM check-in, 6:30 PM check-out).
  // No-ops until notification permission is granted.
  useAttendanceReminders()

  const loadProfile = useCallback(async (user: FirebaseUser) => {
    setProfileError(false)
    try {
      const res = await apiFetch('/api/auth/me')
      const data = await res.json()
      setAppUser(data.user)
      writeCachedProfile(user.uid, data.user)
      setUsingCachedProfile(false)
    } catch (error) {
      // Firebase sign-in succeeded but the backend profile fetch failed (server
      // down, transient 5xx, Firestore read-quota exhausted, non-JSON response).
      console.error('Error fetching app user profile:', error)
      // If we've seen this exact user before, enter the dashboard in a degraded
      // "offline" mode using the cached profile rather than dead-ending at login.
      const cached = readCachedProfile(user.uid)
      if (cached) {
        setAppUser(cached)
        setUsingCachedProfile(true)
        setProfileError(false)
        toast.error('Working offline — some data may be unavailable', { id: 'degraded-mode' })
      } else {
        // No cache to fall back on (first login on this device): show the retry.
        setAppUser(null)
        setUsingCachedProfile(false)
        setProfileError(true)
      }
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user)
      if (user) {
        await loadProfile(user)
      } else {
        setAppUser(null)
        setProfileError(false)
      }
      setLoading(false)
    })
    return () => unsubscribe()
  }, [loadProfile])

  const handleLogout = async () => {
    await signOut(auth)
    clearCachedProfile()
    setAppUser(null)
    setProfileError(false)
    setUsingCachedProfile(false)
  }

  if (loading) {
    return <InfinityLoader />
  }

  // Signed in with Firebase but the profile couldn't be loaded — offer a retry
  // or a way out instead of a dead-end login screen.
  if (firebaseUser && !appUser && profileError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-medium">We couldn&apos;t load your profile</p>
        <p className="text-sm text-neutral-500 max-w-sm">
          You&apos;re signed in, but the server didn&apos;t respond. Check your connection and try again.
        </p>
        <div className="flex gap-3">
          <Button onClick={() => loadProfile(firebaseUser)}>Retry</Button>
          <Button variant="outline" onClick={handleLogout}>Sign out</Button>
        </div>
      </div>
    )
  }

  if (!firebaseUser || !appUser) {
    return <LoginForm />
  }

  // Thin banner shown while running on a cached profile because the backend was
  // unreachable, with a one-tap way to re-fetch once it recovers.
  const offlineBanner = usingCachedProfile ? (
    <div className="sticky top-0 z-50 flex items-center justify-center gap-3 bg-amber-500/90 px-4 py-1.5 text-center text-xs font-medium text-amber-950 backdrop-blur">
      <span>Offline mode — showing your last known profile. Live data may be unavailable.</span>
      <button
        type="button"
        onClick={() => firebaseUser && loadProfile(firebaseUser)}
        className="rounded-md bg-amber-950/10 px-2 py-0.5 font-semibold hover:bg-amber-950/20"
      >
        Retry
      </button>
    </div>
  ) : null

  if (appUser.role === 'ADMIN' || appUser.role === 'HR') {
    return (
      <>
        {offlineBanner}
        <AdminDashboard user={appUser} onLogout={handleLogout} />
        <NotificationPrompt />
      </>
    )
  }

  return (
    <>
      {offlineBanner}
      <EmployeeDashboard user={appUser} onLogout={handleLogout} />
      <NotificationPrompt />
    </>
  )
}

export function App() {
  return (
    <>
      <Toaster
        position="top-right"
        richColors
        theme="dark"
        toastOptions={{
          classNames: {
            toast: 'bg-neutral-900/70 backdrop-blur-xl border border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-white/90 shadow-[0_8px_30px_rgba(0,0,0,0.35)] rounded-xl',
            title: 'text-neutral-900 dark:text-white/90 font-medium',
            description: 'text-neutral-600 dark:text-white/60',
            actionButton: 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white',
            cancelButton: 'bg-white/[0.06] text-neutral-700 dark:text-white/70 hover:bg-white/[0.1]',
            closeButton: 'bg-white/[0.06] border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70',
          },
        }}
      />
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </>
  )
}
